<template>
    <div class="animated fadeIn">
        <Loader :loader="loader" />
        <div v-show="!loader">
            <p class="my-2 text-muted">
                {{ $t('insights.generalDataOfActivePeriods') }}
            </p>
            <b-card-group class="mb-4">
                <b-card>
                    <div class="h1 text-muted text-right mb-4">
                        <i class="icon-speedometer"></i>
                    </div>
                    <div class="h2 mb-0">{{ companyProgress }}%</div>
                    <div class="p text-muted">
                        {{ $t('insights.companyProgress') }}
                    </div>
                    <b-progress
                        height="{}"
                        class="progress-xs mt-3 mb-0"
                        variant="info"
                        :value="companyProgress"
                    />
                </b-card>
                <b-card>
                    <div class="h1 text-muted text-right mb-4">
                        <i class="icon-people"></i>
                    </div>
                    <div class="h2 mb-0">{{ usersCount }}</div>
                    <div class="p text-muted">{{ $t('insights.users') }}</div>
                </b-card>
                <b-card>
                    <div class="h1 text-muted text-right mb-4">
                        <i class="icon-people"></i>
                    </div>
                    <div class="h2 mb-0">{{ usersWithoutKeyResultsCount }}</div>
                    <div class="p text-muted">
                        {{ $t('insights.usersWithoutOKRs') }}
                    </div>
                    <div slot="footer" v-if="usersWithoutKeyResultsCount">
                        <b-link
                            @click="openModal('users-without-okrs')"
                            class="font-weight-bold font-xs btn-block text-muted"
                            href="#"
                        >
                            {{ $t('insights.readMore')
                            }}<i
                                class="fa fa-angle-right float-right font-lg"
                            ></i>
                        </b-link>
                    </div>
                </b-card>
                <b-card>
                    <div v-if="objectivesMoreThan5KeyResults">
                        <div class="h1 text-muted text-right mb-4">
                            <i class="cui-circle-x" style="color: #cb8377"></i>
                        </div>
                        <div class="h5 mb-0 mr-5">
                            {{
                                $t(
                                    'insights.thereAreObjectivesWithMoreThan5KR',
                                    {
                                        numberOfObjectives:
                                            objectivesMoreThan5KeyResults,
                                    }
                                )
                            }}
                        </div>
                    </div>
                    <div v-else>
                        <div class="h1 text-muted text-right mb-4">
                            <i
                                class="cui-circle-check"
                                style="color: #8cb992"
                            ></i>
                        </div>
                        <div class="h5 mb-0 mr-5">
                            {{ $t('insights.noObjectiveHasMoreThan5KR') }}
                        </div>
                    </div>
                    <div slot="footer" v-if="objectivesMoreThan5KeyResults">
                        <b-link
                            @click="
                                openModal('objectives-with-more-than-5-krs')
                            "
                            class="font-weight-bold font-xs btn-block text-muted"
                            href="#"
                        >
                            {{ $t('insights.readMore')
                            }}<i
                                class="fa fa-angle-right float-right font-lg"
                            ></i>
                        </b-link>
                    </div>
                </b-card>
                <b-card>
                    <div v-if="objectivesLessThan2KeyResults">
                        <div class="h1 text-muted text-right mb-4">
                            <i class="cui-circle-x" style="color: #cb8377"></i>
                        </div>
                        <div class="h5 mb-0 mr-5">
                            {{
                                $t(
                                    'insights.thereAreObjectivesWithLessThan2KR',
                                    {
                                        numberOfObjectives:
                                            objectivesLessThan2KeyResults,
                                    }
                                )
                            }}
                        </div>
                    </div>
                    <div v-else>
                        <div class="h1 text-muted text-right mb-4">
                            <i
                                class="cui-circle-check"
                                style="color: #8cb992"
                            ></i>
                        </div>
                        <div class="h5 mb-0 mr-5">
                            {{ $t('insights.noObjectiveHasMoreThan2KR') }}
                        </div>
                    </div>
                    <div slot="footer" v-if="objectivesLessThan2KeyResults">
                        <b-link
                            @click="openModal('objectives-less-than-2-krs')"
                            class="font-weight-bold font-xs btn-block text-muted"
                            href="#"
                        >
                            {{ $t('insights.readMore')
                            }}<i
                                class="fa fa-angle-right float-right font-lg"
                            ></i>
                        </b-link>
                    </div>
                </b-card>
            </b-card-group>
            <b-card>
                <b-row>
                    <b-col sm="6">
                        <h4 id="traffic" class="card-title mb-0">
                            {{ $t('insights.insightsOKR') }}
                        </h4>
                    </b-col>
                    <b-col sm="6" class="d-md-block">
                        <multiselect
                            id="filterOfPeriods"
                            :options="periods"
                            :selected="selectedPeriods"
                            @changeValue="onChangePeriods"
                            :placeholder="$t('form.placeholder.selectPeriods')"
                            :state="null"
                        />
                    </b-col>
                </b-row>
                <b-row class="mt-4 mb-0">
                    <b-col sm="6" md="3" class="mb-3">
                        <b-card class="h-100">
                            <div class="mb-5">
                                <div class="h3 mb-0">
                                    {{ objectivesCompanyCount }}
                                </div>
                                <div class="h5 mt-0">
                                    {{ $t('insights.objectives') }}
                                </div>
                            </div>
                            <div class="p">
                                {{ $t('insights.totalObjectives') }}
                            </div>
                        </b-card>
                    </b-col>
                    <b-col sm="6" md="3" class="mb-3">
                        <b-card class="h-100">
                            <div class="mb-5">
                                <div class="h3 mb-0">
                                    {{ parentlessObjectivesCount }}
                                </div>
                                <div class="h5 mt-0">
                                    {{ $t('insights.objectivesMisaligned') }}
                                </div>
                            </div>
                            <div class="progress-group">
                                <div class="progress-group-header">
                                    <span class="ml-auto font-weight-bold">
                                        {{ porcentageOfParentlessObjectives }}%
                                    </span>
                                </div>
                                <div class="progress-group-bars">
                                    <b-progress
                                        height="{}"
                                        class="progress-xs"
                                        variant="danger"
                                        :value="
                                            porcentageOfParentlessObjectives
                                        "
                                    />
                                </div>
                            </div>
                            <div class="p">
                                {{ $t('insights.totalOfParentlessObjectives') }}
                            </div>
                            <div slot="footer" v-if="parentlessObjectivesCount">
                                <b-link
                                    @click="
                                        openModal(
                                            `parentless-objectives/${periodsIds}`
                                        )
                                    "
                                    class="font-weight-bold font-xs btn-block text-muted"
                                    href="#"
                                >
                                    {{ $t('insights.readMore')
                                    }}<i
                                        class="fa fa-angle-right float-right font-lg"
                                    ></i>
                                </b-link>
                            </div>
                        </b-card>
                    </b-col>
                    <b-col sm="6" md="3" class="mb-3">
                        <b-card class="h-100">
                            <div class="mb-5">
                                <div class="h3 mb-0">
                                    {{ objectivesAchieved }}
                                </div>
                                <div class="h5 mt-0">
                                    {{ $t('insights.achievedObjectives') }}
                                </div>
                            </div>
                            <div class="progress-group">
                                <div class="progress-group-header">
                                    <span class="ml-auto font-weight-bold">
                                        {{ porcentageOfObjectivesAchieved }}%
                                    </span>
                                </div>
                                <div class="progress-group-bars">
                                    <b-progress
                                        height="{}"
                                        class="progress-xs"
                                        variant="success"
                                        :value="porcentageOfObjectivesAchieved"
                                    />
                                </div>
                            </div>
                            <div class="p">
                                {{
                                    $t(
                                        'insights.totalOfObjectivesWithPerfomanceAbove70'
                                    )
                                }}
                            </div>
                        </b-card>
                    </b-col>
                    <b-col sm="6" md="3" class="mb-3">
                        <b-card class="h-100">
                            <div class="mb-5">
                                <div class="h3 mb-0">
                                    {{ objectivesLessThan30Percent }}
                                </div>
                                <div class="h5 mt-0">
                                    {{ $t('insights.objectivesAtRisk') }}
                                </div>
                            </div>
                            <div class="progress-group">
                                <div class="progress-group-header">
                                    <span class="ml-auto font-weight-bold">
                                        {{
                                            porcentageOfObjectivesLessThan30Percent
                                        }}%
                                    </span>
                                </div>
                                <div class="progress-group-bars">
                                    <b-progress
                                        height="{}"
                                        class="progress-xs"
                                        variant="danger"
                                        :value="
                                            porcentageOfObjectivesLessThan30Percent
                                        "
                                    />
                                </div>
                            </div>
                            <div class="p">
                                {{
                                    $t(
                                        'insights.totalOfObjectivesWithPerfomanceBellow30'
                                    )
                                }}
                            </div>
                        </b-card>
                    </b-col>
                    <b-col sm="6" md="3" class="mb-3">
                        <b-card class="h-100">
                            <div class="mb-5">
                                <div class="h3 mb-0">
                                    {{ keyResultsCompanyCount }}
                                </div>
                                <div class="h5 mt-0">
                                    {{ $t('insights.keyResults') }}
                                </div>
                            </div>
                            <div class="p">
                                {{ $t('insights.totalOfKeyResultsKR') }}
                            </div>
                        </b-card>
                    </b-col>
                    <b-col sm="6" md="3" class="mb-3">
                        <b-card class="h-100">
                            <div class="mb-5">
                                <div class="h3 mb-0">
                                    {{ lateKeyResultsCount }}
                                </div>
                                <div class="h5 mt-0">
                                    {{ $t('insights.lateKRs') }}
                                </div>
                            </div>
                            <div class="progress-group">
                                <div class="progress-group-header">
                                    <span class="ml-auto font-weight-bold">
                                        {{ porcentageOfLateKeyResults }}%
                                    </span>
                                </div>
                                <div class="progress-group-bars">
                                    <b-progress
                                        height="{}"
                                        class="progress-xs"
                                        variant="danger"
                                        :value="porcentageOfLateKeyResults"
                                    />
                                </div>
                            </div>
                            <div class="p">
                                {{ $t('insights.totalOfKRWithLateCheckin') }}
                            </div>
                            <div slot="footer" v-if="lateKeyResultsCount">
                                <b-link
                                    @click="
                                        openModal(
                                            `late-key-results/${periodsIds}`
                                        )
                                    "
                                    class="font-weight-bold font-xs btn-block text-muted"
                                    href="#"
                                >
                                    {{ $t('insights.readMore')
                                    }}<i
                                        class="fa fa-angle-right float-right font-lg"
                                    ></i>
                                </b-link>
                            </div>
                        </b-card>
                    </b-col>
                    <b-col sm="6" md="3" class="mb-3">
                        <b-card class="h-100">
                            <div class="mb-5">
                                <div class="h3 mb-0">
                                    {{ keyResultsAchieved }}
                                </div>
                                <div class="h5 mt-0">
                                    {{ $t('insights.achievedKR') }}
                                </div>
                            </div>
                            <div class="progress-group">
                                <div class="progress-group-header">
                                    <span class="ml-auto font-weight-bold">
                                        {{ porcentageOfKeyResultsAchieved }}%
                                    </span>
                                </div>
                                <div class="progress-group-bars">
                                    <b-progress
                                        height="{}"
                                        class="progress-xs"
                                        variant="success"
                                        :value="porcentageOfKeyResultsAchieved"
                                    />
                                </div>
                            </div>
                            <div class="p">
                                {{ $t('insights.totalOFKRAbove70OrConcluded') }}
                            </div>
                        </b-card>
                    </b-col>
                    <b-col sm="6" md="3" class="mb-3">
                        <b-card class="h-100">
                            <div class="mb-5">
                                <div class="h3 mb-0">
                                    {{ keyResultsLessThan20Percent }}
                                </div>
                                <div class="h5 mt-0">
                                    {{ $t('insights.KRAtRisk') }}
                                </div>
                            </div>
                            <div class="progress-group">
                                <div class="progress-group-header">
                                    <span class="ml-auto font-weight-bold">
                                        {{
                                            porcentageOfKeyResultsLessThan20Percent
                                        }}%
                                    </span>
                                </div>
                                <div class="progress-group-bars">
                                    <b-progress
                                        height="{}"
                                        class="progress-xs"
                                        variant="danger"
                                        :value="
                                            porcentageOfKeyResultsLessThan20Percent
                                        "
                                    />
                                </div>
                            </div>
                            <div class="p">
                                {{
                                    $t('insights.allKRWithPerformanceBellow20')
                                }}
                            </div>
                        </b-card>
                    </b-col>
                </b-row>
            </b-card>
            <router-view @modalClosed="onModalClose()"></router-view>
        </div>
    </div>
</template>

<script>
import api from '@/services/api'
import Loader from '@/views/components/loader'
import { Multiselect, mapperToOptions } from '@/views/components/selects'
import { getActivePeriods } from '@/lib/periods'
import { decimalFormat } from '@/lib/utils'
import { getUserPermissions } from '@/services/user-permissions'

export default {
    name: 'Insights',
    components: {
        Loader,
        Multiselect,
    },
    data() {
        return {
            periods: [],
            selectedPeriods: [],
            periodsIds: '',
            loader: true,
            insightsData: {
                company_progress: 0,
                users_count: 0,
                users_without_key_results_count: 0,
                objectives_more_than_5_key_results: 0,
                objectives_less_than_2_key_results: 0,
                objectives_company_count: 0,
                parentless_objectives_count: 0,
                objectives_achieved: 0,
                objectives_less_than_30_percent: 0,
                key_results_company_count: 0,
                late_key_results_count: 0,
                key_results_achieved: 0,
                key_results_less_than_20_percent: 0,
            },
        }
    },
    computed: {
        companyProgress() {
            return decimalFormat(this.insightsData.company_progress * 100)
        },
        usersCount() {
            return this.insightsData.users_count
        },
        usersWithoutKeyResultsCount() {
            return this.insightsData.users_without_key_results_count
        },
        objectivesMoreThan5KeyResults() {
            return this.insightsData.objectives_more_than_5_key_results
        },
        objectivesLessThan2KeyResults() {
            return this.insightsData.objectives_less_than_2_key_results
        },
        objectivesCompanyCount() {
            return this.insightsData.objectives_company_count
        },
        parentlessObjectivesCount() {
            return this.insightsData.parentless_objectives_count
        },
        porcentageOfParentlessObjectives() {
            return decimalFormat(
                this.calculatePercentageByObjetivesCount(
                    this.parentlessObjectivesCount
                )
            )
        },
        objectivesAchieved() {
            return this.insightsData.objectives_achieved
        },
        porcentageOfObjectivesAchieved() {
            return decimalFormat(
                this.calculatePercentageByObjetivesCount(
                    this.objectivesAchieved
                )
            )
        },
        objectivesLessThan30Percent() {
            return this.insightsData.objectives_less_than_30_percent
        },
        porcentageOfObjectivesLessThan30Percent() {
            return decimalFormat(
                this.calculatePercentageByObjetivesCount(
                    this.objectivesLessThan30Percent
                )
            )
        },
        keyResultsCompanyCount() {
            return this.insightsData.key_results_company_count
        },
        lateKeyResultsCount() {
            return this.insightsData.late_key_results_count
        },
        porcentageOfLateKeyResults() {
            return decimalFormat(
                this.calculatePercentageByKRCount(this.lateKeyResultsCount)
            )
        },
        keyResultsAchieved() {
            return this.insightsData.key_results_achieved
        },
        porcentageOfKeyResultsAchieved() {
            return decimalFormat(
                this.calculatePercentageByKRCount(this.keyResultsAchieved)
            )
        },
        keyResultsLessThan20Percent() {
            return this.insightsData.key_results_less_than_20_percent
        },
        porcentageOfKeyResultsLessThan20Percent() {
            return decimalFormat(
                this.calculatePercentageByKRCount(
                    this.keyResultsLessThan20Percent
                )
            )
        },
    },
    methods: {
        calculatePercentageBetweenTwoValues(denominator, numerator) {
            return numerator ? (100 * numerator) / denominator : 0
        },
        calculatePercentageByObjetivesCount(val) {
            return this.calculatePercentageBetweenTwoValues(
                this.objectivesCompanyCount,
                val
            )
        },
        calculatePercentageByKRCount(val) {
            return this.calculatePercentageBetweenTwoValues(
                this.keyResultsCompanyCount,
                val
            )
        },
        getPeriodsIds(arrayOfPeriods) {
            return arrayOfPeriods.map((period) => period.value).join(',')
        },
        onChangePeriods(values) {
            this.periodsIds = this.getPeriodsIds(values)
            this.getDataFromAPI()
        },
        openModal(route) {
            this.$router.push({
                path: `insights/${route}`,
            })
        },
        onModalClose() {
            this.getDataFromAPI()
        },
        getDataFromAPI() {
            api.get('/dashboard', {
                params: {
                    period_id: this.periodsIds,
                },
            })
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp
                    this.insightsData = data
                })
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    this.loader = false
                })
        },
    },
    mounted() {
        const {
            is_admin,
            is_values_master,
            is_target_master,
            is_god,
            is_local_administrator,
            is_okr_leader,
        } = getUserPermissions()

        if (
            !is_admin &&
            !is_values_master &&
            !is_target_master &&
            !is_god &&
            !is_okr_leader &&
            !is_local_administrator
        ) {
            this.$router.push({
                path: '/v2/pages/error/403',
            })
        }
        api.get('/periods', {
            params: {
                scopes: 'notArchived',
                orderBy: 'end_date',
                sortedBy: 'desc',
            },
        })
            .then((resp) => {
                const {
                    data: { data },
                } = resp
                this.periods = mapperToOptions(data, undefined, 'name')
                this.selectedPeriods = getActivePeriods(this.periods)
                this.periodsIds = this.getPeriodsIds(this.selectedPeriods)
                this.getDataFromAPI()
            })
            .catch((e) => {
                console.log(e)
            })
    },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
